import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import {
  applyIdCambioFromApi,
  isAcabadoOnlyCatalogChange,
} from '@context/utils/linked/linkedUnitVariants'
import { invalidateLinkedTextureCaches } from '@context/utils/linked/invalidateLinkedTextureCaches'
import {
  applyLinkedUnitOptionsPatch,
  mapLinkedUnitOptionsResponse,
} from '@utils/sceneObjects/linked/fetchLinkedUnitOptions'
import { rebuildLinkedItemZonesFromUnit } from '@utils/sceneObjects/linked/rebuildLinkedItemZonesFromUnit'
import type { LinkedVariantPrefetchBundle } from '@utils/sceneObjects/linked/prefetchLinkedVariantCatalogs'
import type { LinkedItem, Morphs, Store } from '@types'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { adjustChildrenPositionsAfterInheritance } from '@utils/sceneObjects/items/adjustChildrenToEncimera'

export type ApplyLinkedGlbVariantOptions = {
  linkedOptionsByIdUnidades?: Map<number, Record<string, unknown>>
  skipSonsUpdate?: boolean
  /**
   * Conservar ancho/alto/fondo + morphs + free-dim + flags de zócalo del estado actual.
   * Default: true en cambios solo de acabado (LA/LI/Ru/R2[/GO]).
   */
  preserveSceneLayout?: boolean
  updateLinkedSonsForNewGLB?: (
    itemId: string,
    newCatalogId: string,
    previousCatalogId?: string,
  ) => Promise<unknown>
}

function normalizeCatalogCode(catalogId: string): string {
  return sceneGLBLookupKey(catalogId)
}

function isVitrinaCatalog(codeKey: string): boolean {
  const upper = codeKey.toUpperCase()
  return (
    upper.startsWith('KABV') ||
    upper.startsWith('KAPV') ||
    upper.startsWith('PE KABV') ||
    upper.startsWith('PE KAPV')
  )
}

function stripAcabadoSuffix(catalogID: string): string {
  return catalogID.replace(/ (GO|LA GO|RU GO|R2 GO| LI| LA| RU| R2)/, '')
}

type LayoutSnapshot = {
  width?: number
  height?: number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: LinkedItem['freeDimensionCatalogMm']
  freeDimensionGlbBaseMm?: LinkedItem['freeDimensionGlbBaseMm']
  freeDimensionScaleBaseMm?: LinkedItem['freeDimensionScaleBaseMm']
  freeDimensionAnchoViaGlbMorph?: boolean
  freeDimensionAltoViaGlbMorph?: boolean
  freeDimensionFondoViaGlbMorph?: boolean
  hasIndependentZocalo?: boolean
  independentZocaloSonId?: string
  independentZocaloReturnSonId?: string
  independentZocaloHeightMm?: number
  independentZocaloFloorOffsetApplied?: boolean
}

function snapshotSceneLayout(item: LinkedItem): LayoutSnapshot {
  return {
    width: item.width,
    height: item.height,
    thickness: item.thickness,
    morphs: item.morphs ? item.morphs.map((m) => ({ ...m })) : undefined,
    freeDimensionCatalogMm: item.freeDimensionCatalogMm
      ? { ...item.freeDimensionCatalogMm }
      : undefined,
    freeDimensionGlbBaseMm: item.freeDimensionGlbBaseMm
      ? { ...item.freeDimensionGlbBaseMm }
      : undefined,
    freeDimensionScaleBaseMm: item.freeDimensionScaleBaseMm
      ? { ...item.freeDimensionScaleBaseMm }
      : undefined,
    freeDimensionAnchoViaGlbMorph: item.freeDimensionAnchoViaGlbMorph,
    freeDimensionAltoViaGlbMorph: item.freeDimensionAltoViaGlbMorph,
    freeDimensionFondoViaGlbMorph: item.freeDimensionFondoViaGlbMorph,
    hasIndependentZocalo: item.hasIndependentZocalo,
    independentZocaloSonId: item.independentZocaloSonId,
    independentZocaloReturnSonId: item.independentZocaloReturnSonId,
    independentZocaloHeightMm: item.independentZocaloHeightMm,
    independentZocaloFloorOffsetApplied: item.independentZocaloFloorOffsetApplied,
  }
}

function morphCurrentMm(m: Morphs): number | undefined {
  if (typeof m._default === 'number' && Number.isFinite(m._default)) return m._default
  if (typeof m.default === 'number' && Number.isFinite(m.default)) return m.default
  return undefined
}

/**
 * Fusiona morphs de la unidad nueva conservando el valor actual (Ancho/Alto/Fondo…).
 * Luego escribe width/height/thickness desde el layout preservado (no desde catálogo API).
 */
function restoreSceneLayout(
  item: LinkedItem,
  snapshot: LayoutSnapshot,
  unidadData: Record<string, unknown>,
): void {
  const preservedByName = new Map<string, number>()
  snapshot.morphs?.forEach((m) => {
    const v = morphCurrentMm(m)
    if (m.name && v != null) preservedByName.set(m.name, v)
  })
  // Si no hay morphs, al menos Ancho/Alto/Fondo desde dims store.
  if (snapshot.width != null && Number.isFinite(snapshot.width)) {
    preservedByName.set('Ancho', snapshot.width)
  }
  if (snapshot.height != null && Number.isFinite(snapshot.height)) {
    preservedByName.set('Alto', snapshot.height)
  }
  if (snapshot.thickness != null && Number.isFinite(snapshot.thickness)) {
    preservedByName.set('Fondo', snapshot.thickness)
  }

  const apiMorphs = unidadData.morphs
  if (Array.isArray(apiMorphs) && apiMorphs.length > 0) {
    item.morphs = apiMorphs.map((morph: Record<string, unknown>) => {
      const name = String(morph.nombre ?? morph.name ?? '')
      const min = Number(morph.valorMin ?? morph.min ?? 0)
      const max = Number(morph.valorMax ?? morph.max ?? 0)
      const base = Number(morph.valor ?? morph.default ?? 0)
      let next = base
      if (preservedByName.has(name)) {
        const preserved = preservedByName.get(name) as number
        if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
          next = Math.max(min, Math.min(max, preserved))
        } else {
          next = preserved
        }
      }
      return {
        _default: next,
        default: next,
        max,
        min,
        modifyable: true,
        morphId: (morph.idMorph ?? morph.id ?? morph.morphId) as number,
        name,
        order: (morph.orden ?? morph.order ?? 0) as number,
        show: true,
        type: String(morph.tipo ?? morph.type ?? ''),
      } satisfies Morphs
    })
  } else if (snapshot.morphs?.length) {
    // Sin morphs en API: conservar los del item (free-dim / sintéticos).
    item.morphs = snapshot.morphs.map((m) => {
      const preserved = m.name ? preservedByName.get(m.name) : undefined
      if (preserved == null) return { ...m }
      return { ...m, default: preserved, _default: preserved }
    })
  }

  if (snapshot.width != null && Number.isFinite(snapshot.width)) item.width = snapshot.width
  if (snapshot.height != null && Number.isFinite(snapshot.height)) item.height = snapshot.height
  if (snapshot.thickness != null && Number.isFinite(snapshot.thickness)) {
    item.thickness = snapshot.thickness
  }

  // Alinear morph Ancho/Alto/Fondo con dims store (por si el clamp cambió el morph).
  item.morphs?.forEach((m) => {
    if (m.name === 'Ancho' && item.width != null) {
      m.default = item.width
      m._default = item.width
    } else if (m.name === 'Alto' && item.height != null) {
      m.default = item.height
      m._default = item.height
    } else if (m.name === 'Fondo' && item.thickness != null) {
      m.default = item.thickness
      m._default = item.thickness
    }
  })

  if (snapshot.freeDimensionCatalogMm) {
    item.freeDimensionCatalogMm = snapshot.freeDimensionCatalogMm
  }
  if (snapshot.freeDimensionGlbBaseMm) {
    item.freeDimensionGlbBaseMm = snapshot.freeDimensionGlbBaseMm
  }
  if (snapshot.freeDimensionScaleBaseMm) {
    item.freeDimensionScaleBaseMm = snapshot.freeDimensionScaleBaseMm
  }
  if (snapshot.freeDimensionAnchoViaGlbMorph != null) {
    item.freeDimensionAnchoViaGlbMorph = snapshot.freeDimensionAnchoViaGlbMorph
  }
  if (snapshot.freeDimensionAltoViaGlbMorph != null) {
    item.freeDimensionAltoViaGlbMorph = snapshot.freeDimensionAltoViaGlbMorph
  }
  if (snapshot.freeDimensionFondoViaGlbMorph != null) {
    item.freeDimensionFondoViaGlbMorph = snapshot.freeDimensionFondoViaGlbMorph
  }

  if (snapshot.hasIndependentZocalo != null) {
    item.hasIndependentZocalo = snapshot.hasIndependentZocalo
  }
  if (snapshot.independentZocaloSonId != null) {
    item.independentZocaloSonId = snapshot.independentZocaloSonId
  }
  if (snapshot.independentZocaloReturnSonId != null) {
    item.independentZocaloReturnSonId = snapshot.independentZocaloReturnSonId
  }
  if (snapshot.independentZocaloHeightMm != null) {
    item.independentZocaloHeightMm = snapshot.independentZocaloHeightMm
  }
  if (snapshot.independentZocaloFloorOffsetApplied != null) {
    item.independentZocaloFloorOffsetApplied = snapshot.independentZocaloFloorOffsetApplied
  }
}

/**
 * Aplica un cambio de variante GLB a un linkedItem usando datos precargados (sin API ni loadAsync).
 * En cambios de acabado especial (LA/LI/Ru/R2) conserva tamaño y zócalo: solo cambia puerta/GLB/zonas.
 */
export function applyLinkedItemGlbVariantInStore(
  store: Store,
  itemId: string,
  newCatalogId: string,
  bundle: LinkedVariantPrefetchBundle,
  previousCatalogId: string,
  options?: ApplyLinkedGlbVariantOptions,
): boolean {
  const item = store.items.get(itemId)
  if (!item || !isLinkedItem(item)) return false

  const codeKey = bundle.codeKey
  const unidadData = bundle.unidadData
  const glb = bundle.glb

  const preserveLayout =
    options?.preserveSceneLayout ??
    (isAcabadoOnlyCatalogChange(previousCatalogId, codeKey) ||
      isAcabadoOnlyCatalogChange(previousCatalogId, newCatalogId))

  const layoutSnapshot = preserveLayout ? snapshotSceneLayout(item) : null

  if (!store.sceneGLB.has(codeKey)) {
    store.sceneGLB.set(codeKey, { glb, backGLBInfo: unidadData })
  }

  item.catalogID = codeKey

  const ref = unidadData.referenciaBlender
  if (typeof ref === 'string' && ref) {
    item.glbPath = ref
  }
  if (typeof unidadData.referencia === 'string') {
    item.referencia = unidadData.referencia
  }
  if (typeof unidadData.referenciaJpgpng === 'string') {
    item.image = unidadData.referenciaJpgpng
  }
  if (unidadData.idUnidades != null) {
    item.idUnidades = unidadData.idUnidades as number
    invalidateLinkedTextureCaches()
  }
  if (typeof unidadData.descripcion === 'string') {
    item.description = unidadData.descripcion as typeof item.description
  }
  if (typeof unidadData.descripcionLarga === 'string') {
    item.long = unidadData.descripcionLarga
  }

  // Dimensiones catálogo solo si NO preservamos layout (p. ej. cambio de base real).
  if (!preserveLayout) {
    if (typeof unidadData.ancho === 'number') {
      item.width = unidadData.ancho
    }
    if (typeof unidadData.fondo === 'number') {
      item.thickness = unidadData.fondo
    }
    if (typeof unidadData.alto === 'number') {
      item.height = unidadData.alto
    }
  }

  applyIdCambioFromApi(item as Record<string, unknown>, unidadData)
  item.golaActiva = codeKey.includes(' GO')

  const revision = ((item as LinkedItem & { glbRevision?: number }).glbRevision ?? 0) + 1
  ;(item as LinkedItem & { glbRevision?: number }).glbRevision = revision
  item.materialEpoch = (item.materialEpoch ?? 0) + 1
  // No pisar glbSize real: rompe scaleY free-dim (mismo bug que en laterales).
  // Si hace falta invalidar AABB, reconstruir desde freeDimensionGlbBaseMm.
  const glbMm = item.freeDimensionGlbBaseMm
  if (glbMm?.x && glbMm?.y) {
    item.glbSize = {
      x: glbMm.x / 1000,
      y: glbMm.y / 1000,
      z: (glbMm.z > 0 ? glbMm.z : glbMm.x) / 1000,
    } as typeof item.glbSize
  }

  const userAppliedTexturesByZoneName = new Map<string, unknown>()
  item.textures?.forEach((texture) => {
    if (texture.zoneName) {
      userAppliedTexturesByZoneName.set(texture.zoneName, texture)
    }
  })

  rebuildLinkedItemZonesFromUnit({
    item,
    newUnidadData: unidadData,
    glbSceneChildren: glb.scene?.children,
    acabadosZonasExtra: bundle.acabadosZonasExtra,
    userAppliedTexturesByZoneName: userAppliedTexturesByZoneName as Map<string, never>,
    isVitrina: isVitrinaCatalog(codeKey),
    itemId,
    source: 'applyLinkedItemGlbVariant',
  })

  if (layoutSnapshot) {
    restoreSceneLayout(item, layoutSnapshot, unidadData)
  }

  const idUnidades = item.idUnidades
  if (idUnidades && options?.linkedOptionsByIdUnidades?.has(idUnidades)) {
    const payload = options.linkedOptionsByIdUnidades.get(idUnidades)!
    const patch = mapLinkedUnitOptionsResponse(payload)
    applyLinkedUnitOptionsPatch(item, patch, { preserveExisting: true })
  }

  const normalizedNewCatalogID = normalizeCatalogCode(newCatalogId)
  const baseCatalogID = stripAcabadoSuffix(previousCatalogId)
  const baseNewCatalogID = stripAcabadoSuffix(normalizedNewCatalogID)

  if (
    !options?.skipSonsUpdate &&
    previousCatalogId !== normalizedNewCatalogID &&
    baseCatalogID !== baseNewCatalogID &&
    options?.updateLinkedSonsForNewGLB
  ) {
    // Llamada async fuera del set — el batch la orquesta después
    return true
  }

  if (previousCatalogId !== normalizedNewCatalogID && baseCatalogID === baseNewCatalogID) {
    item.childs?.forEach((childID) => {
      const child = store.items.get(childID)
      if (!child || child.type !== 'linkedSon' || child.description === 'Tirador') return
      const x = child.x ?? 0
      const y = child.y ?? 0
      const z = child.z ?? 0
      if (!(x === 0 && y === 0 && z === 0)) {
        child.visible = true
      }
    })
  }

  return true
}

export async function finalizeLinkedItemGlbVariantSideEffects(
  get: () => Store,
  editItem: (id: string, updates: Record<string, unknown>) => void,
  itemId: string,
  newCatalogId: string,
  previousCatalogId: string,
  options?: ApplyLinkedGlbVariantOptions,
): Promise<void> {
  const normalizedNewCatalogID = normalizeCatalogCode(newCatalogId)
  const baseCatalogID = stripAcabadoSuffix(previousCatalogId)
  const baseNewCatalogID = stripAcabadoSuffix(normalizedNewCatalogID)

  if (
    previousCatalogId !== normalizedNewCatalogID &&
    baseCatalogID !== baseNewCatalogID &&
    options?.updateLinkedSonsForNewGLB
  ) {
    await options.updateLinkedSonsForNewGLB(itemId, newCatalogId, previousCatalogId)
  }

  const updatedItem = get().items.get(itemId)
  if (
    updatedItem &&
    isLinkedItem(updatedItem) &&
    updatedItem.childs &&
    Array.isArray(updatedItem.childs) &&
    updatedItem.childs.length > 0
  ) {
    adjustChildrenPositionsAfterInheritance(
      updatedItem,
      (childId) => get().items.get(childId),
      (childId, updates) => {
        editItem(childId, updates)
      },
    )
  }
}
